import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Eye, EyeOff } from 'lucide-react';
import { useProfile } from '../../../Contexts/ProfileContext'; // adjust path as needed
import { postChangePassword, verifyOldPassword } from '../../../services/userService'; // adjust path as needed

interface FormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const PasswordSecurity: React.FC = () => {
  const profile = useProfile();

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');

  const [oldPasswordError, setOldPasswordError] = useState('');
  const [checkingOldPassword, setCheckingOldPassword] = useState(false);

  const initialValues: FormValues = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  const validationSchema = Yup.object({
    oldPassword: Yup.string().required('Old password is required'),
    newPassword: Yup.string().min(8, 'Password must be at least 8 characters').required('New password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Please confirm your password'),
  });

  const checkOldPassword = async (password: string) => {
    if (!password.trim()) {
      setOldPasswordError('');
      return;
    }

    setCheckingOldPassword(true);

    try {
      const response = await verifyOldPassword({
        old_password: password,
      });

      if (response.valid) {
        // Password is correct
        setOldPasswordError('');
      } else {
        // Password is incorrect
        setOldPasswordError('Old password is incorrect.');
      }
    } catch (error) {
      setOldPasswordError('Unable to verify old password. Please try again.');
    } finally {
      setCheckingOldPassword(false);
    }
  };

  const handleSubmit = async (values: FormValues, { resetForm }: any) => {
    if (!profile?.id) {
      setMessageType('error');
      setMessage('User not found. Please log in again.');
      return;
    }

    setIsSaving(true);
    setMessage('');

    try {
      const payload = {
        old_password: values.oldPassword,
        password: values.newPassword,
        password_confirmation: values.confirmPassword,
      };

      await postChangePassword(profile.id, payload);

      setMessageType('success');
      setMessage('Password updated successfully!');
      resetForm();

      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error(error);

      let errorMessage = 'Something went wrong.';

      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const firstKey = Object.keys(errors)[0];

        if (firstKey && errors[firstKey]?.length) {
          errorMessage = errors[firstKey][0];
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setMessageType('error');
      setMessage(errorMessage);

      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col items-start justify-center bg-gray-50">
      <div className="w-full bg-white p-8 rounded-lg shadow-sm">
        <p className="text-gray-700 mb-8 text-base">
          Update your password through the button below. You will be redirected to a new page and must follow the instructions.
        </p>
        <h2 className="text-lg font-semibold text-red-600 border-b-2 border-red-500 inline-block mb-6">Set New Password</h2>

        {message && (
          <div
            className={`mb-4 px-4 py-2 rounded-md text-sm ${
              messageType === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}
          >
            {message}
          </div>
        )}

        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values }) => (
            <Form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Old Password */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Old Password</label>
                  <div className="relative">
                    <Field
                      name="oldPassword"
                      type={showOld ? 'text' : 'password'}
                      className="w-full border border-gray-300 rounded-md pl-4 pr-12 py-2 bg-gray-50 focus:outline-none"
                      placeholder="Enter old password"
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        checkOldPassword(e.target.value);
                      }}
                    />
                  </div>
                  <ErrorMessage name="oldPassword" component="p" className="text-red-500 text-sm mt-1" />

                  {checkingOldPassword && <p className="text-gray-500 text-sm mt-1">Checking password...</p>}

                  {oldPasswordError && <p className="text-red-500 text-sm mt-1">{oldPasswordError}</p>}
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2">New Password</label>
                  <div className="relative">
                    <Field
                      name="newPassword"
                      type={showNew ? 'text' : 'password'}
                      className="w-full border border-gray-300 rounded-md pl-4 pr-12 py-2 bg-gray-50 focus:outline-none"
                      placeholder="Enter new password"
                    />
                  </div>
                  <ErrorMessage name="newPassword" component="p" className="text-red-500 text-sm mt-1" />
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">Confirm Password</label>
                <div className="relative">
                  <Field
                    name="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    className="w-full border border-gray-300 rounded-md pl-4 pr-12 py-2 bg-gray-50 focus:outline-none"
                    placeholder="Confirm new password"
                  />
                  {/*<button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute inset-y-0 right-3 flex items-center text-gray-500">
                    {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>*/}
                </div>
                <ErrorMessage name="confirmPassword" component="p" className="text-red-500 text-sm mt-1" />
              </div>

              <button
                type="submit"
                disabled={isSaving}
                className={`text-white font-semibold px-6 py-2 rounded-md transition-all ${
                  isSaving ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSaving ? 'Updating...' : 'Update Password'}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default PasswordSecurity;
